import * as React from 'react'
import { cn } from '../../../../../lib/utils'
import { ViewSelector, ViewType } from '../ViewSelector'
import { QueryField, SectionRelationConfig } from '../../index'
import type { CalendarSyncDirection } from '../../../DataView/Calendar/types'

interface QueryLayoutProps {
  className?: string
  view?: ViewType
  isFocused?: boolean
  onViewChange?: (view: ViewType) => void
  onOpenChange?: (open: boolean) => void
  onSortChange?: (
    sort:
      | { key: string; direction?: 'asc' | 'desc' }
      | Array<{ key: string; direction?: 'asc' | 'desc' }>,
  ) => void
  fields: (QueryField & { isSelected: boolean; showModelLabel?: boolean })[]
  sectionableFields?: QueryField[]
  sort?:
    | { key: string; direction?: 'asc' | 'desc' }
    | Array<{ key: string; direction?: 'asc' | 'desc' }>
  onFieldSelectionChange?: (
    field: QueryField,
    isSelected: boolean,
    allSelectedValues: string[],
  ) => void
  onSectionChange?: (field: QueryField | null, groupKey?: string) => void
  onSubsectionChange?: (field: QueryField | null, groupKey?: string) => void
  selectedSectionLabel?: string
  selectedSubsectionLabel?: string
  pagination?: {
    page: number
    total: number
    hasMore: boolean
    limit: number
  }
  modelName?: string
  // Calendar-specific configuration
  calendarStartField?: QueryField | null
  calendarEndField?: QueryField | null
  calendarRecurrenceField?: QueryField | null
  calendarSeriesIdField?: QueryField | null
  calendarOriginalStartField?: QueryField | null
  calendarField?: QueryField | null
  calendarFieldMap?: Record<string, string>
  queryFields?: QueryField[]
  onCalendarStartFieldChange?: (field: QueryField | null) => void
  onCalendarEndFieldChange?: (field: QueryField | null) => void
  onCalendarRecurrenceFieldChange?: (field: QueryField | null) => void
  onCalendarSeriesIdFieldChange?: (field: QueryField | null) => void
  onCalendarOriginalStartFieldChange?: (field: QueryField | null) => void
  onCalendarFieldChange?: (field: QueryField | null) => void
  onCalendarFieldMapChange?: (next: Record<string, string>) => void
  // Kanban-specific configuration
  kanbanStatusBadgeField?: QueryField | null
  kanbanDescriptionField?: QueryField | null
  onKanbanStatusBadgeFieldChange?: (field: QueryField | null) => void
  onKanbanDescriptionFieldChange?: (field: QueryField | null) => void
  // Optional configuration for RELATION-based "section by" fields
  sectionRelationConfig?: SectionRelationConfig
  calendarSyncEnabled?: boolean
  calendarSyncDirection?: CalendarSyncDirection
  calendarSyncExternalReadOnly?: boolean
  calendarSyncCalendarIds?: string[]
  onCalendarSyncEnabledChange?: (enabled: boolean) => void
  onCalendarSyncDirectionChange?: (direction: CalendarSyncDirection) => void
  onCalendarSyncExternalReadOnlyChange?: (readOnly: boolean) => void
  onCalendarSyncCalendarIdsChange?: (calendarIds: string[]) => void
}

export const QueryLayout = ({
  className,
  view = 'spreadsheet',
  isFocused = false,
  fields,
  sectionableFields,
  onViewChange,
  onOpenChange,
  onSortChange,
  sort,
  onFieldSelectionChange,
  onSectionChange,
  onSubsectionChange,
  selectedSectionLabel,
  selectedSubsectionLabel,
  pagination,
  modelName,
  calendarStartField,
  calendarEndField,
  calendarRecurrenceField,
  calendarSeriesIdField,
  calendarOriginalStartField,
  calendarField,
  calendarFieldMap,
  queryFields,
  onCalendarStartFieldChange,
  onCalendarEndFieldChange,
  onCalendarRecurrenceFieldChange,
  onCalendarSeriesIdFieldChange,
  onCalendarOriginalStartFieldChange,
  onCalendarFieldChange,
  onCalendarFieldMapChange,
  kanbanStatusBadgeField,
  kanbanDescriptionField,
  onKanbanStatusBadgeFieldChange,
  onKanbanDescriptionFieldChange,
  sectionRelationConfig,
  calendarSyncEnabled,
  calendarSyncDirection,
  calendarSyncExternalReadOnly,
  calendarSyncCalendarIds,
  onCalendarSyncEnabledChange,
  onCalendarSyncDirectionChange,
  onCalendarSyncExternalReadOnlyChange,
  onCalendarSyncCalendarIdsChange,
}: QueryLayoutProps) => {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <ViewSelector
        onOpenChange={(open) => onOpenChange?.(open)}
        isFocused={isFocused}
        view={view}
        onViewChange={(newView) => onViewChange?.(newView)}
        onSortChange={onSortChange}
        fields={fields}
        sectionableFields={sectionableFields}
        sort={sort}
        onFieldSelectionChange={onFieldSelectionChange}
        onSectionChange={onSectionChange}
        onSubsectionChange={onSubsectionChange}
        selectedSectionLabel={selectedSectionLabel}
        selectedSubsectionLabel={selectedSubsectionLabel}
        pagination={pagination}
        modelName={modelName}
        calendarStartField={calendarStartField || null}
        calendarEndField={calendarEndField || null}
        calendarRecurrenceField={calendarRecurrenceField || null}
        calendarSeriesIdField={calendarSeriesIdField || null}
        calendarOriginalStartField={calendarOriginalStartField || null}
        calendarField={calendarField || null}
        calendarFieldMap={calendarFieldMap}
        queryFields={queryFields}
        onCalendarStartFieldChange={onCalendarStartFieldChange}
        onCalendarEndFieldChange={onCalendarEndFieldChange}
        onCalendarRecurrenceFieldChange={onCalendarRecurrenceFieldChange}
        onCalendarSeriesIdFieldChange={onCalendarSeriesIdFieldChange}
        onCalendarOriginalStartFieldChange={
          onCalendarOriginalStartFieldChange
        }
        onCalendarFieldChange={onCalendarFieldChange}
        onCalendarFieldMapChange={onCalendarFieldMapChange}
        kanbanStatusBadgeField={kanbanStatusBadgeField || null}
        kanbanDescriptionField={kanbanDescriptionField || null}
        onKanbanStatusBadgeFieldChange={onKanbanStatusBadgeFieldChange}
        onKanbanDescriptionFieldChange={onKanbanDescriptionFieldChange}
        calendarSyncEnabled={calendarSyncEnabled}
        calendarSyncDirection={calendarSyncDirection}
        calendarSyncExternalReadOnly={calendarSyncExternalReadOnly}
        calendarSyncCalendarIds={calendarSyncCalendarIds}
        onCalendarSyncEnabledChange={onCalendarSyncEnabledChange}
        onCalendarSyncDirectionChange={onCalendarSyncDirectionChange}
        onCalendarSyncExternalReadOnlyChange={
          onCalendarSyncExternalReadOnlyChange
        }
        onCalendarSyncCalendarIdsChange={onCalendarSyncCalendarIdsChange}
        sectionRelationConfig={sectionRelationConfig}
      />
    </div>
  )
}
